import React, { FC, useState } from 'react';
import { IconButton, Menu, Box, Typography, Button, Divider } from '@mui/material';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface DateRangeProps {
  startDate: string | null;
  endDate: string | null;
  setStartDate: (date: string | null) => void;
  setEndDate: (date: string | null) => void;
  onApply?: (startDate: string | null, endDate: string | null) => void;
  onReset?: () => void;
  label: string;
}

const DateRange: FC<DateRangeProps> = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  onApply,
  onReset,
  label,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [datesApplied, setDatesApplied] = useState(false);

  const isOpen = Boolean(anchorEl);
  const menuId = 'settlement-date-filter-menu';

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setDatesApplied(false);
    handleClose();
    if (onReset) onReset();
  };

  const handleApply = () => {
    setDatesApplied(true);
    handleClose();
    if (onApply) onApply(startDate, endDate);
  };

  return (
    <>
      <Box
        sx={{
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.25rem',
        }}
      >
        {datesApplied && (
          <Box
            sx={{
              width: '1.25rem',
              height: '1.25rem',
              bgcolor: '#FF6347',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: '#fff',
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              1
            </Typography>
          </Box>
        )}
        <IconButton
          size="small"
          onClick={handleClick}
          aria-label={label}
          aria-controls={isOpen ? menuId : undefined}
          aria-haspopup="true"
          aria-expanded={isOpen ? 'true' : undefined}
        >
          <FilterAltOutlinedIcon sx={{ fontSize: '1.25rem', color: '#162C36' }} />
        </IconButton>
      </Box>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        MenuListProps={{ sx: { padding: 0 } }}
      >
        <Box sx={{ p: 2, width: '24rem' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: 'flex',
                gap: '1rem',
                mb: 2,
                width: '100%',
              }}
            >
              <DatePicker
                label="Start Date"
                value={startDate ? dayjs(startDate, 'YYYY-MM-DD') : null}
                onChange={(value: Dayjs | null) =>
                  setStartDate(value ? value.format('YYYY-MM-DD') : null)
                }
                maxDate={endDate ? dayjs(endDate, 'YYYY-MM-DD') : undefined}
                format="YYYY-MM-DD"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                    width: '100%',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#737F86',
                  },
                }}
              />
              <DatePicker
                label="End Date"
                value={endDate ? dayjs(endDate, 'YYYY-MM-DD') : null}
                onChange={(value: Dayjs | null) =>
                  setEndDate(value ? value.format('YYYY-MM-DD') : null)
                }
                minDate={startDate ? dayjs(startDate, 'YYYY-MM-DD') : undefined}
                format="YYYY-MM-DD"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '1rem',
                    fontWeight: 500,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#737F86',
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
              gap: 2,
              fontSize: '0.875rem',
              fontWeight: 700,
              color: '#51B8B0',
            }}
          >
            <Button sx={{ all: 'unset', cursor: 'pointer' }} onClick={handleReset}>
              Reset
            </Button>
            <Button
              sx={{
                all: 'unset',
                cursor: 'pointer',
              }}
              onClick={handleApply}
            >
              OK
            </Button>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

export default DateRange;
